import { useState, useRef } from 'react';
import { Send, Paperclip, X, Eye } from 'lucide-react';
import type { Comment, CommentAttachment } from '../../types';
import { useApp } from '../../context/AppContext';
import { formatRelativeDate, isPreviewableFile } from '../../utils/helpers';
import Avatar from '../common/Avatar';
import FilePreviewModal from '../common/FilePreviewModal';
import RichTextEditor from '../common/RichTextEditor';

interface TaskCommentsProps {
  taskId: string;
  comments: Comment[];
}

export default function TaskComments({ taskId, comments }: TaskCommentsProps) {
  const { state, getUserById, apiAddComment } = useApp();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<CommentAttachment | null>(null);

  const handleSubmit = async () => {
    const stripped = text.replace(/<[^>]*>/g, '').trim();
    if (!stripped && files.length === 0) return;

    const attachments = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
    }));

    await apiAddComment(taskId, text, attachments);
    setText('');
    setFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    setFiles((prev) => [...prev, ...Array.from(selected)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-700 mb-3">
        Comentários ({comments.length})
      </h4>

      <div className="space-y-4 mb-4 max-h-72 overflow-y-auto">
        {comments.map((comment) => {
          const user = getUserById(comment.userId);
          return (
            <div key={comment.id} className="flex gap-3 animate-slide-in-up">
              <Avatar
                name={user?.name || 'Unknown'}
                color={user?.avatarColor || '#94a3b8'}
                size="md"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-700">
                    {user?.name || 'Unknown'}
                  </span>
                  <span className="text-xs text-slate-400">
                    {formatRelativeDate(comment.createdAt)}
                  </span>
                </div>
                <div
                  className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none break-words [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 [&_a]:text-brand-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: comment.content }}
                />
                {comment.attachments && comment.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {comment.attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-100 text-xs text-slate-600"
                      >
                        <Paperclip size={12} className="text-slate-400" />
                        <span className="truncate max-w-[120px]">{att.name}</span>
                        {isPreviewableFile(att.type) && (
                          <button
                            onClick={() => setPreviewFile(att)}
                            className="p-0.5 rounded text-slate-400 hover:text-brand-500 transition-colors"
                            aria-label={`Visualizar ${att.name}`}
                          >
                            <Eye size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {comments.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">Nenhum comentário ainda</p>
        )}
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-brand-50 text-xs text-brand-700"
            >
              <Paperclip size={12} />
              <span className="truncate max-w-[120px]">{file.name}</span>
              <button
                onClick={() => removeFile(index)}
                className="p-0.5 rounded text-brand-400 hover:text-red-500 transition-colors"
                aria-label={`Remover ${file.name}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex gap-2 items-start">
          <Avatar
            name={state.currentUser?.name || 'Usuário'}
            color={state.currentUser?.avatarColor || '#94a3b8'}
            size="md"
          />
          <div className="flex-1">
            <RichTextEditor
              value={text}
              onChange={setText}
              placeholder="Escreva um comentário..."
              minHeight="80px"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <label className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-brand-500 hover:border-brand-300 cursor-pointer transition-colors">
            <Paperclip size={16} />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="sr-only"
              aria-label="Anexar arquivo"
            />
          </label>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!text.replace(/<[^>]*>/g, '').trim() && files.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Enviar comentário"
          >
            <Send size={14} />
            Enviar
          </button>
        </div>
      </div>

      {previewFile && (
        <FilePreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileName={previewFile.name}
          fileType={previewFile.type}
          fileUrl={previewFile.url}
        />
      )}
    </div>
  );
}
